import { Component, ElementRef, ChangeDetectorRef } from '@angular/core';
import { StateService } from '@uirouter/core';
import { cloneDeep } from 'lodash';
import { environment } from 'src/environments/environment';
import { ModalService } from 'src/app/components/modals/modal.service';
import { ComponentsService } from '../../services/components.service';
import { AttributeDataService } from '../../services/attribute-data.service';
import { TemplateEditorUtilsService } from '../../services/template-editor-utils.service';
import { TemplateEditorService } from '../../services/template-editor.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { GoogleCalendarService } from '../services/google-calendar.service';
import { MicrosoftOutlookService } from '../services/microsoft-outlook.service';
import { OAuthService } from 'src/app/components/content-oauth/services/oauth.service';
import { FeaturesService } from 'src/app/components/plans/features.service';
import { IntegrationsCompanyDialogsService } from 'src/app/settings/services/integrations/integrations-company/integrations-company-dialogs.service';
import {
  CompanyIntegration,
  IntegrationsCompanyService,
} from 'src/app/settings/services/integrations/integrations-company/integrations-company.service';
import { ComponentUtilsService } from '../services/component-utils.service';
import { companyIntegrationOpenTarget } from 'src/app/settings/services/integrations/integrations-company/integrations-company.routing';
import { CalendarBase } from '../calendar-base/calendar.base';

@Component({
  selector: 'template-component-layout-ems',
  templateUrl: './layout-ems.component.html',
  styleUrls: ['./layout-ems.component.scss'],
  standalone: false,
})
export class LayoutEmsComponent extends CalendarBase {
  protected readonly layoutComponent = 'rise-layout-ems';

  protected override readonly DEFAULT_PERIOD = 'days';
  private static readonly DEFAULT_LAYOUT_ID =
    ComponentsService.LAYOUTS_MAP['rise-layout-ems'].ROOM_SCHEDULE;

  public readonly PROVIDER_ADASTRA = 'adastra';
  public readonly SINGLE_ROOM_PROVIDERS = [
    this.PROVIDER_GOOGLE_CALENDAR,
    this.PROVIDER_OUTLOOK_CALENDAR,
  ];

  LayoutType = ComponentsService.LAYOUTS_MAP['rise-layout-ems']; // make layout types available in template
  public filterByRoom: boolean = false;
  private componentServices: any;

  private signature: string;

  public providerCredentialsMissing: boolean = false;

  constructor(
    private elementRef: ElementRef,
    changeDetectorRef: ChangeDetectorRef,
    private stateService: StateService,
    modalService: ModalService,
    private componentsService: ComponentsService,
    private attributeDataService: AttributeDataService,
    private templateEditorUtils: TemplateEditorUtilsService,
    templateEditorService: TemplateEditorService,
    private companyStateService: CompanyStateService,
    userStateService: UserStateService,
    googleCalendarService: GoogleCalendarService,
    microsoftOutlookService: MicrosoftOutlookService,
    authService: OAuthService,
    private featuresService: FeaturesService,
    private integrationsCompanyService: IntegrationsCompanyService,
    private integrationsCompanyDialogsService: IntegrationsCompanyDialogsService,
    private componentUtilsService: ComponentUtilsService,
  ) {
    super(
      changeDetectorRef,
      googleCalendarService,
      microsoftOutlookService,
      authService,
      userStateService,
      modalService,
      templateEditorService,
    );
    this.componentsService.registerDirective({
      type: 'rise-layout-ems',
      element: this.elementRef.nativeElement,
      show: async (options?) => {
        this.spinner = false;
        if (this.componentsService.selected?.id) {
          this.componentId = this.componentsService.selected.id;
          this.load();
          this.userAccount = this.authService.getUserIdentifier(null);
          if (this.isPublicUrl()) {
            this.validateCalendarUrl();
          } else {
            await this.validateCredentials();
          }
        }
      },
    });
  }

  load() {
    this.reset();

    const defaultAttributes =
      this.componentsService.getSelectedDirective()?.defaultAttributes || {};
    const defaultLayout = cloneDeep(defaultAttributes?.layout || {});
    const defaultServices = cloneDeep(defaultAttributes?.services || []);

    const componentLayout =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'layout',
      ) || defaultLayout;
    const componentServices =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'services',
      ) || defaultServices;

    // account for situation when "layout" and "services" are set as JSON strings in html template
    this.componentLayout = this.templateEditorUtils.safeParseJson(
      componentLayout,
      defaultLayout,
    );
    this.componentServices = this.templateEditorUtils.safeParseJson(
      componentServices,
      defaultServices,
    );

    const layoutAttributes = this.componentLayout?.attributes || {};
    const serviceAttributes =
      Array.isArray(this.componentServices) && this.componentServices.length > 0
        ? this.componentServices[0]?.attributes || {}
        : {};

    this.calendarUrlStatus = null;
    this.provider = serviceAttributes.provider ?? null;
    this.componentAccount = serviceAttributes.account ?? '';
    this.source = serviceAttributes.source ?? '';
    this.outlookGroupId = serviceAttributes.groupId ?? '';
    this.selectedOutlookGroup = this.outlookGroupId || '';
    this.selectedOutlookGroupName = serviceAttributes.groupName ?? '';
    this.loadLayoutSelection(LayoutEmsComponent.DEFAULT_LAYOUT_ID);
    this.filterByRoom = serviceAttributes.filterByRoom ?? false;
    this.range = serviceAttributes.range ?? this.DEFAULT_RANGE;
    this.period = serviceAttributes.period ?? this.DEFAULT_PERIOD;
    const hideCompleted = serviceAttributes.completed ?? true; // Default differs from calendar component
    this.showCompleted = !hideCompleted;
    this.showDescription = layoutAttributes.showDescription ?? true;
    this.color = layoutAttributes.color ?? this.DEFAULT_DISPLAY_COLOR;
    this.backgroundColor =
      layoutAttributes.backgroundColor ?? this.DEFAULT_DISPLAY_BACKGROUND_COLOR;

    this.selectedName = serviceAttributes.name ?? '';
    if (this.selectedName) {
      this.selectedCalendar = this.source || '';
    } else {
      this.selectedCalendar = '';
    }

    this.calendarType = serviceAttributes.calendarType ?? null;
    this.signature = serviceAttributes.signature ?? '';

    this.updateValidRange();

    // if environment is not defined, then this is a new component
    // re-save all settings including environment
    const env = this.attributeDataService.getAttributeData(
      this.componentId,
      'environment',
    );
    if (!env) {
      this.save();
    }
  }

  protected override onOutlookSingleCalendarAutoSelected(): void {
    this.selectedCalendarChanged();
  }

  protected override extraProviderDisplayName(
    provider: string | null,
  ): string | null {
    if (provider === this.PROVIDER_ADASTRA) {
      return 'Ad Astra';
    }
    return null;
  }

  protected override extraProviderLogo(provider: string | null): string | null {
    if (provider === this.PROVIDER_ADASTRA) {
      return 'integration-ad-astra';
    }
    return null;
  }

  protected override async validateCredentials(): Promise<void> {
    if (this.provider === this.PROVIDER_ADASTRA) {
      this.providerCredentialsMissing = !this.checkProviderCredentials();
      return;
    }
    await super.validateCredentials();
  }

  async validateCalendarUrl(): Promise<boolean> {
    if (!this.source) {
      return true;
    }

    this.spinner = true;
    let isValid = false;
    try {
      const provider = this.getProviderFromUrl(this.source);
      switch (provider) {
        case this.PROVIDER_GOOGLE_CALENDAR: {
          const result = await this.googleCalendarService.validate(this.source);
          isValid = result === 'VALID';
          break;
        }
        case this.PROVIDER_ICAL:
          isValid = this.validateIcalUrl();
          break;
        default:
          isValid = false;
      }

      if (isValid) {
        this.provider = provider;
      }
    } finally {
      this.spinner = false;
    }

    this.calendarUrlStatus = isValid ? 'VALID' : 'INVALID';
    this.changeDetectorRef.detectChanges();

    return isValid;
  }

  public override save() {
    const previousServiceAttrs = this.componentServices[0]?.attributes ?? {};
    const previousSource = previousServiceAttrs.source ?? '';
    let sourceToSave = this.source;
    if (this.isPublicUrl() && this.calendarUrlStatus === 'INVALID') {
      sourceToSave = previousSource;
    }

    let rangeToSave = this.templateEditorUtils.intValueFor(
      this.range,
      this.DEFAULT_RANGE,
    );
    let periodToSave = this.period;
    if (!this.isValidRange()) {
      rangeToSave = previousServiceAttrs.range ?? this.DEFAULT_RANGE;
      periodToSave = previousServiceAttrs.period ?? this.DEFAULT_PERIOD;
    }
    const accountToSave = this._getAccountToSave();
    const signatureToSave = this._getSignatureToSave();

    this.applyLayoutSelection();
    this.componentLayout.attributes = {
      showDescription: this.showDescription,
      color: this.color,
      backgroundColor: this.backgroundColor,
    };

    if (
      !Array.isArray(this.componentServices) ||
      this.componentServices.length === 0
    ) {
      this.componentServices = [{}];
    }
    this.componentServices[0].attributes = {
      provider: this.provider,
      calendarType: this.calendarType,
      account: accountToSave,
      source: sourceToSave,
      name: this.selectedName,
      groupId: this.outlookGroupId,
      groupName: this.selectedOutlookGroupName,
      range: rangeToSave,
      period: periodToSave,
      completed: !this.showCompleted,
      filterByRoom: this.filterByRoom,
      mode: 'ems',
      signature: signatureToSave,
    };

    this.attributeDataService.setAttributeData(
      this.componentId,
      'layout',
      this.componentLayout,
    );
    this.attributeDataService.setAttributeData(
      this.componentId,
      'services',
      this.componentServices,
    );
    this.attributeDataService.setAttributeData(
      this.componentId,
      'environment',
      environment.production ? 'prod' : 'test',
    );
  }

  filterByRoomChanged(checked: boolean): void {
    this.filterByRoom = checked;
    // reset layout (but never convert a custom component to stock)
    if (!this.filterByRoom && !this.isCustomLayout) {
      this.layout = LayoutEmsComponent.DEFAULT_LAYOUT_ID;
    }
    this.save();
  }

  override reset() {
    const wasCustom = this.isCustomLayout;
    super.reset();
    this.providerCredentialsMissing = false;
    this.filterByRoom = false;
    this.signature = '';

    if (!wasCustom) {
      this.layout = LayoutEmsComponent.DEFAULT_LAYOUT_ID;
    }

    this.updateValidRange();
  }

  async selectCalendarType(calendarType: string) {
    if (!this.featuresService.isFeatureAvailable(FeaturesService.FEATURE_EMS)) {
      this.featuresService.showUpgradePlanModal(FeaturesService.FEATURE_EMS);
      return;
    }

    // reset auth errors
    this.reset();

    this.calendarType = calendarType;

    if (calendarType !== this.CALENDAR_TYPE_PUBLIC) {
      this.provider = calendarType;
      // Note, for public URL provider is set during URL validation.
    }

    const oauth = this.USER_AUTHORIZED_PROVIDERS as readonly string[];
    if (oauth.includes(calendarType)) {
      await this.connectAccount();
      if (this.authenticateFailed || !this.componentAccount) {
        this.calendarType = null;
        this.provider = null;
      }
    } else if (calendarType === this.PROVIDER_ADASTRA) {
      await this._connectProviderAccount();
      await this._createSignature(this.PROVIDER_ADASTRA);
    }

    this.save();
  }

  private checkProviderCredentials(): boolean {
    const company = this.companyStateService.getCopyOfSelectedCompany(true);
    const adastraEnabled =
      company?.settings?.adastraEnabled === true ||
      company?.settings?.adastraEnabled === 'true';
    return !!adastraEnabled;
  }

  private async _connectProviderAccount() {
    if (this.checkProviderCredentials()) {
      // do nothing if provider credentials are already set
      this.providerCredentialsMissing = false;
      return;
    }

    //check user permissions to update company settings
    //if not, user can still configure component, but they'll see an error that credentials are missing
    if (!this.userStateService.hasPermission('ua sa')) {
      this.providerCredentialsMissing = true;
      return;
    }

    const integration = this.integrationsCompanyService.findById('adastra');
    if (!integration) {
      this.providerCredentialsMissing = true;
      return;
    }

    let credentialsMissing = true;
    this.spinner = true;
    this.changeDetectorRef.detectChanges();
    try {
      await this.integrationsCompanyService.load();
    } finally {
      // Clear before awaiting the dialog so the spinner does not cover it.
      this.spinner = false;
      this.changeDetectorRef.detectChanges();
    }

    const values = await this.integrationsCompanyDialogsService.promptConfigure(
      integration,
      true,
    );
    if (values && values !== 'navigating-away') {
      this.spinner = true;
      this.changeDetectorRef.detectChanges();
      try {
        await this.saveAdAstraIntegration(integration, values);
        credentialsMissing = !this.checkProviderCredentials();
      } finally {
        this.spinner = false;
        this.changeDetectorRef.detectChanges();
      }
    }

    this.providerCredentialsMissing = credentialsMissing;
  }

  private async saveAdAstraIntegration(
    integration: CompanyIntegration,
    values: Record<string, string>,
  ): Promise<void> {
    const enabledKey = integration.enabledKey;
    if (!enabledKey) {
      return;
    }

    try {
      await this.integrationsCompanyService.saveIntegrationSettings(
        enabledKey,
        values,
        this.integrationsCompanyService.exclusiveKeysToDisable(integration),
      );
      this.integrationsCompanyDialogsService.showSuccessSnackbar(
        'Connected',
        integration.name,
      );
    } catch (error) {
      console.error('Failed to save company integration:', error);
      this.integrationsCompanyDialogsService.showErrorSnackbar(
        `Failed to connect ${integration.name} integration.`,
      );
    }
  }

  setLayout(layout: string) {
    this.layout = layout;
    this.save();
  }

  isSingleRoomProvider() {
    return (
      !!this.provider && this.SINGLE_ROOM_PROVIDERS.includes(this.provider)
    );
  }

  openIntegrationsPage(event?: Event) {
    event?.preventDefault();
    const target = companyIntegrationOpenTarget(this.PROVIDER_ADASTRA);
    this.stateService.go(target.state, target.params);
  }

  private _getAccountToSave(): string {
    const oauth = this.USER_AUTHORIZED_PROVIDERS as readonly string[];
    if (this.provider && oauth.includes(this.provider) && !this.isPublicUrl()) {
      return this.componentAccount ?? '';
    }

    return '';
  }

  private _getSignatureToSave(): string {
    if (this.provider === this.PROVIDER_ADASTRA) {
      return this.signature;
    }

    return '';
  }

  private async _createSignature(provider: string) {
    const company = this.companyStateService.getCopyOfSelectedCompany(true);

    this.signature = await this.componentUtilsService.getSignature(
      company.id,
      provider,
    );
  }
}
